import { useState, useCallback, useEffect, useMemo } from "react";
import { callClaude, searchPlace } from "./shared/api/client";

// ─── Constants ────────────────────────────────────────────────────────────────

const TONES = [
    { id: "professional", label: "Professional & Data-Driven" },
    { id: "aggressive", label: "Aggressive Growth" },
    { id: "partnership", label: "Partnership & Rescue" }
];

// ─── Prompt Engineering ───────────────────────────────────────────────────────

const buildSystemPrompt = (tone) => `
You are an elite Business Development AI for a SaaS company operating in Saudi Arabia (KSA).
Your task: analyze merchant customer reviews (both historical and real-time Google Places data) and return a structured JSON merchant profile.

STRICT RULES:
1. Respond ONLY with a single valid JSON object — no markdown, no explanation, no code fences.
2. All Arabic text must be Modern Standard Arabic (فصحى) unless a field specifies dialect.
3. Every field is REQUIRED. Never omit or null any key.
4. Scores must be integers between 1 and 10 inclusive.

TONE SETTING:
The pitch should be written in a ${tone} tone.

JSON SCHEMA (return exactly these keys):
{
  "sentiment_score": <integer 1-10, overall customer sentiment>,
  "top_praise": "<single most praised aspect, in Arabic>",
  "top_complaint": "<single most critical complaint, in Arabic>",
  "service_highlights": ["<top dish/activity/item mentioned positively, Arabic>", ...],
  "bd_priority_score": <integer 1-10, how urgently our BD team should target this merchant>,
  "objection_handling": [
    { "objection": "<likely merchant objection, Arabic>", "response": "<persuasive BD counter-argument, Arabic>" }
  ],
  "foot_in_the_door": "<a highly specific, low-friction initial offer or promotion we can pitch based on current market demands/reviews, Arabic>",
  "competitive_insight": "<brief analysis of where they stand compared to local standards and how our platform bridges the gap, Arabic>",
  "arabic_sales_pitch": "<full persuasive B2B sales script in Arabic, 3-5 sentences, addressing pain points, highlighting ROI, and ending with a CTA>"
}
`.trim();

const buildUserPrompt = (merchantName, dbReviews, googleData) => `
Merchant Name: ${merchantName}
Database Customer Reviews:
${dbReviews}

Fresh Google Places Data & Reviews:
${googleData ? JSON.stringify(googleData, null, 2) : 'Not available'}

Analyze ALL the above data and return the JSON profile now.
`.trim();

// ─── API Layer ─────────────────────────────────────────────────────────

async function fetchGooglePlacesData(query) {
    try {
        // Authenticated via the shared API client (adds Bearer token, timeout,
        // standardized errors). Response shape { place, places } is unchanged.
        const data = await searchPlace({ query });
        return data.place;
    } catch (err) {
        console.error("Google Places Error:", err);
        throw new Error("Live place enrichment is temporarily unavailable");
    }
}


// Helper to extract JSON from a string that might contain other text or markdown
function extractJson(str) {
    const start = str.indexOf('{');
    const end = str.lastIndexOf('}');
    if (start === -1 || end === -1 || end < start) {
        throw new Error("No valid JSON object found in response");
    }
    return str.slice(start, end + 1);
}

async function fetchClaudeProfile({ merchantName, dbReviews, googleData, tone, onChunk }) {
    // Authenticated via the shared API client; returns the raw streaming (SSE)
    // Response, and throws ApiError on non-2xx (so no manual !ok check needed).
    const response = await callClaude({
        system: buildSystemPrompt(tone),
        prompt: buildUserPrompt(merchantName, dbReviews, googleData),
        stream: true,
    });

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let accumulated = "";

    // Parse Server-Sent Events (SSE)
    while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
            if (line.startsWith('data: ') && line !== 'data: [DONE]') {
                try {
                    const data = JSON.parse(line.slice(6));
                    if (data.type === 'content_block_delta' && data.delta?.text) {
                        accumulated += data.delta.text;
                        onChunk?.(accumulated);
                    }
                } catch (e) {
                    // Ignore parse errors on partial chunks
                }
            }
        }
    }

    try {
        const cleanJsonStr = extractJson(accumulated);
        return JSON.parse(cleanJsonStr);
    } catch (e) {
        console.error("JSON Parse Error. Accumulated string:", accumulated);
        throw new Error("Failed to parse Claude output as valid JSON. The AI response might have been malformed or truncated.");
    }
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function ToggleGroup({ options, active, onChange, disabled }) {
    return (
        <div style={styles.toggleGroup}>
            {options.map((m) => (
                <button
                    key={m.id}
                    onClick={() => onChange(m.id)}
                    disabled={disabled}
                    style={{
                        ...styles.toggleBtn,
                        ...(active === m.id ? styles.toggleBtnActive : {}),
                    }}
                >
                    {m.label}
                </button>
            ))}
        </div>
    );
}

function ProfileCard({ result, label }) {
    if (!result) return null;

    const scoreColor = (n) =>
        n >= 8 ? "#16a34a" : n >= 5 ? "#d97706" : "#dc2626";

    return (
        <div style={styles.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h3 style={styles.cardTitle}>{label}</h3>
            </div>

            <div style={styles.scoreRow}>
                <ScoreBadge label="Sentiment" value={result.sentiment_score} color={scoreColor(result.sentiment_score)} />
                <ScoreBadge label="BD Priority" value={result.bd_priority_score} color={scoreColor(result.bd_priority_score)} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
                <Field label="Top Praise 👍" value={result.top_praise} arabic />
                <Field label="Top Complaint ⚠️" value={result.top_complaint} arabic />
            </div>

            <div style={styles.field}>
                <span style={styles.fieldLabel}>Service/Menu Highlights 🌟</span>
                <div style={styles.pills}>
                    {result.service_highlights?.map((item, i) => (
                        <span key={i} style={styles.pill}>{item}</span>
                    ))}
                </div>
            </div>

            <div style={styles.pitchBox}>
                <span style={styles.fieldLabel}>Competitive Insight 📊</span>
                <p style={styles.pitchTextLight}>{result.competitive_insight}</p>
            </div>

            <div style={styles.pitchBox}>
                <span style={styles.fieldLabel}>Foot-in-the-Door Strategy 🎯</span>
                <p style={styles.pitchTextLight}>{result.foot_in_the_door}</p>
            </div>

            <div style={{ marginTop: 16 }}>
                <span style={styles.fieldLabel}>Objection Handling 🛡️</span>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {result.objection_handling?.map((obj, i) => (
                        <div key={i} style={styles.objectionCard}>
                            <div style={styles.objectionText}><strong>They say:</strong> {obj.objection}</div>
                            <div style={styles.responseText}><strong>You say:</strong> {obj.response}</div>
                        </div>
                    ))}
                </div>
            </div>

            <div style={{ ...styles.pitchBox, background: "#fff7ed", borderColor: "#fed7aa", marginTop: 16 }}>
                <span style={styles.fieldLabel}>Arabic Sales Pitch 📞</span>
                <p style={{ ...styles.pitchText, color: "#9a3412" }}>{result.arabic_sales_pitch}</p>
            </div>
        </div>
    );
}

function ScoreBadge({ label, value, color }) {
    return (
        <div style={styles.scoreBadge}>
            <span style={{ ...styles.scoreNum, color }}>{value}/10</span>
            <span style={styles.scoreLabel}>{label}</span>
        </div>
    );
}

function Field({ label, value, arabic }) {
    return (
        <div style={styles.field}>
            <span style={styles.fieldLabel}>{label}</span>
            <span style={arabic ? styles.arabicText : {}}>{value}</span>
        </div>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function MerchantProfiler({ initialMerchant, initialPlaceData, embedded, tickets = [], reviewSample = null }) {
    const [activeTone, setActiveTone] = useState(TONES[0].id);
    const [merchantName, setMerchantName] = useState(initialMerchant ? initialMerchant.Merchant : "");
    const [reviews, setReviews] = useState(initialMerchant ? (initialMerchant.Reviews3 || "") : "");

    // Google Places State
    const [googlePlace, setGooglePlace] = useState(null);
    const [isFetchingPlaces, setIsFetchingPlaces] = useState(false);
    const [placesError, setPlacesError] = useState("");

    // AI Analysis State
    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [streamText, setStreamText] = useState("");
    const [error, setError] = useState(null);

    useEffect(() => {
        if (initialMerchant) {
            setMerchantName(initialMerchant.Merchant || "");
            const captured = (reviewSample?.reviews || []).filter((review) => review.has_written_text).map((review) => review.review_text || review.original_text || review.translated_text).filter(Boolean).join("\n---\n");
            setReviews(captured ? `Captured Google review sample — not all reviews.\n${captured}` : (initialMerchant.Reviews3 || ""));
            setResult(null);
            setStreamText("");
            setError(null);
            setPlacesError("");

            if (initialPlaceData) {
                // Branch was selected from pills — use its data directly, skip re-fetch
                setGooglePlace(initialPlaceData);
            } else {
                setGooglePlace(null);
                if (initialMerchant.Merchant) {
                    fetchPlaces(initialMerchant.Merchant + (initialMerchant.City ? " " + initialMerchant.City : ""));
                }
            }
        }
    }, [initialMerchant, initialPlaceData, reviewSample]);

    const fetchPlaces = async (query) => {
        setIsFetchingPlaces(true);
        setPlacesError("");
        try {
            const place = await fetchGooglePlacesData(query);
            setGooglePlace(place);
        } catch (err) {
            setGooglePlace(null);
            setPlacesError(err?.message || "Live place enrichment is temporarily unavailable");
        } finally { setIsFetchingPlaces(false); }
    };

    // --- CRM Integration ---
    const merchantTickets = useMemo(() => {
        if (!merchantName) return [];
        const n = merchantName.toLowerCase();
        return tickets.filter(t => 
            (t.merchantName && t.merchantName.toLowerCase().includes(n)) || 
            (t.subject && t.subject.toLowerCase().includes(n))
        ).sort((a, b) => b.createdTime.localeCompare(a.createdTime));
    }, [merchantName, tickets]);

    const crmStats = useMemo(() => {
        if (!merchantTickets.length) return null;
        const analyzed = merchantTickets.filter(t => t.aiStatus === "completed");
        return {
            total: merchantTickets.length,
            churnRisk: merchantTickets.some(t => t.isChurnIntent),
            avgScore: analyzed.length ? (analyzed.reduce((s, t) => s + t.overallQualityScore, 0) / analyzed.length).toFixed(1) : null,
            topIssue: analyzed[0]?.issueType || "Mixed",
            monetaryImpact: merchantTickets.reduce((s, t) => s + (t.monetaryValue || 0), 0)
        };
    }, [merchantTickets]);

    const handleAnalyze = useCallback(async () => {
        if (!merchantName.trim()) {
            setError("Please enter a merchant name.");
            return;
        }
        setLoading(true);
        setStreamText("");
        setError(null);

        try {
            const profile = await fetchClaudeProfile({
                merchantName,
                dbReviews: reviews,
                googleData: googlePlace,
                tone: activeTone,
                onChunk: (text) => setStreamText(text),
            });
            setResult(profile);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [merchantName, reviews, googlePlace, activeTone]);

    return (
        <div style={embedded ? { width: "100%", padding: 20, boxSizing: "border-box" } : styles.root}>
            {!embedded && (
                <>
                    <h2 style={styles.title}>AI Merchant Profiler</h2>
                    <p style={styles.subtitle}>Powered by Claude Sonnet 4.6 & Google Places API</p>
                </>
            )}

            <div style={{ display: "grid", gridTemplateColumns: merchantTickets.length > 0 ? "1fr 320px" : "1fr", gap: 24 }}>
                <div>
                    {/* Tone Controls */}
                    <div style={styles.controls}>
                        <ToggleGroup
                            options={TONES}
                            active={activeTone}
                            onChange={setActiveTone}
                            disabled={loading}
                        />
                    </div>

                    {/* Inputs & Live Data */}
                    <div style={styles.inputGroup}>
                        <div style={{ display: "flex", gap: 8 }}>
                            <input
                                style={{ ...styles.input, flex: 1 }}
                                placeholder="Merchant name (e.g. مطعم الأصيل)"
                                value={merchantName}
                                onChange={(e) => setMerchantName(e.target.value)}
                            />
                            <button
                                onClick={() => fetchPlaces(merchantName)}
                                disabled={isFetchingPlaces || !merchantName}
                                style={{ ...styles.toggleBtn, border: "1px solid #d1d5db" }}
                            >
                                {isFetchingPlaces ? "Fetching..." : "Fetch Live Data"}
                            </button>
                        </div>

                        {placesError && (
                            <div role="status" style={{ marginTop: 8, padding: "9px 12px", borderRadius: 8, background: "#FFF7ED", border: "1px solid #FED7AA", color: "#9A3412", fontSize: 12 }}>
                                Core merchant data is still available. Live place enrichment failed for this merchant.
                                <button type="button" onClick={() => fetchPlaces(merchantName)} style={{ marginLeft: 8, border: 0, background: "transparent", color: "inherit", textDecoration: "underline", cursor: "pointer", fontWeight: 700 }}>Retry enrichment</button>
                            </div>
                        )}
                        
                        {/* Live Google Data Banner */}
                        {googlePlace && (
                            <div style={styles.liveDataBanner}>
                                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 8 }}>
                                    <div style={{ width: 40, height: 40, background: "#E5E7EB", borderRadius: 8, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
                                        📍
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 600, fontSize: 14 }}>{googlePlace.displayName?.text || merchantName}</div>
                                        <div style={{ fontSize: 11, color: "#6B7280" }}>{googlePlace.formattedAddress}</div>
                                    </div>
                                    <div style={{ marginLeft: "auto", textAlign: "right" }}>
                                        {googlePlace.rating && (
                                            <div style={{ fontSize: 14, fontWeight: 700, color: "#16A34A" }}>
                                                ★ {googlePlace.rating}
                                            </div>
                                        )}
                                        {googlePlace.userRatingCount && (
                                            <div style={{ fontSize: 11, color: "#6B7280" }}>
                                                {googlePlace.userRatingCount.toLocaleString()} reviews
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {googlePlace.websiteUri && (
                                    <a href={googlePlace.websiteUri} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: "#1d4ed8", textDecoration: "none", display: "inline-block", marginTop: 4 }}>
                                        🔗 Visit Website / Menu
                                    </a>
                                )}
                            </div>
                        )}

                        <textarea
                            style={styles.textarea}
                            placeholder="Database customer reviews (Arabic or English)…"
                            rows={4}
                            value={reviews}
                            onChange={(e) => setReviews(e.target.value)}
                        />
                    </div>

                    <button
                        style={{ ...styles.analyzeBtn, opacity: loading ? 0.6 : 1 }}
                        onClick={handleAnalyze}
                        disabled={loading}
                    >
                        {loading ? "Analyzing Strategy…" : "Run Deep Analysis ✨"}
                    </button>

                    {/* Error Banner */}
                    {error && (
                        <div style={styles.errorBanner}>
                            <strong>Error:</strong> {error}
                        </div>
                    )}

                    {/* Results */}
                    <>
                        {loading && (
                            <div style={styles.loadingBox}>
                                <div style={styles.spinner} />
                                <div style={{ marginTop: 20 }}>
                                    <div style={{ fontSize: 16, fontWeight: 700, color: "#111827", marginBottom: 4 }}>Analyzing Market Presence...</div>
                                    <div style={{ fontSize: 13, color: "#6B7280" }}>Synthesizing strategy and handling objections</div>
                                </div>
                                {/* Hidden debug stream */}
                                <div style={{ display: "none" }}>{streamText}</div>
                            </div>
                        )}
                        <ProfileCard
                            result={result}
                            label="Strategic BD Profile"
                        />
                    </>
                </div>

                {/* CRM Support History Section (unchanged) */}
                {merchantTickets.length > 0 && (
                    <div style={{ borderLeft: "1px solid #e5e7eb", paddingLeft: 24 }}>
                        <h3 style={{ fontSize: 14, fontWeight: 700, marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
                            <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" /></svg>
                            CRM Support History
                        </h3>

                        {crmStats && (
                            <div style={{ background: "#F9FAFB", borderRadius: 12, padding: 16, marginBottom: 20 }}>
                                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
                                    <div>
                                        <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>Total Tickets</div>
                                        <div style={{ fontSize: 20, fontWeight: 700 }}>{crmStats.total}</div>
                                    </div>
                                    {crmStats.avgScore && (
                                        <div>
                                            <div style={{ fontSize: 10, color: "#6B7280", textTransform: "uppercase" }}>Avg AI Quality</div>
                                            <div style={{ fontSize: 20, fontWeight: 700, color: crmStats.avgScore >= 7 ? "#16A34A" : "#D97706" }}>{crmStats.avgScore}</div>
                                        </div>
                                    )}
                                </div>
                                {crmStats.churnRisk && (
                                    <div style={{ background: "#FEE2E2", color: "#DC2626", padding: "8px 12px", borderRadius: 8, fontSize: 12, fontWeight: 600, display: "flex", alignItems: "center", gap: 6, marginBottom: 12 }}>
                                        ⚠️ Churn Risk Detected
                                    </div>
                                )}
                                {crmStats.monetaryImpact > 0 && (
                                    <div style={{ fontSize: 11, color: "#6B7280" }}>
                                        Total Monetary Impact: <span style={{ fontWeight: 600, color: "#111827" }}>{crmStats.monetaryImpact.toLocaleString()} SAR</span>
                                    </div>
                                )}
                            </div>
                        )}

                        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                            <div style={{ fontSize: 10, fontWeight: 700, color: "#9CA3AF", textTransform: "uppercase" }}>Recent Tickets</div>
                            {merchantTickets.slice(0, 5).map(t => (
                                <div key={t.id} style={{ padding: 12, borderRadius: 10, border: "1px solid #F3F4F6", background: "#FFF" }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                        <span style={{ fontSize: 11, fontWeight: 600, color: "#374151" }}>#{t.id}</span>
                                        <span style={{ fontSize: 10, color: "#9CA3AF" }}>{t.createdTime.slice(0, 10)}</span>
                                    </div>
                                    <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 6, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{t.subject}</div>
                                    <div style={{ display: "flex", gap: 4 }}>
                                        {t.finalSentiment && (
                                            <span style={{ 
                                                fontSize: 9, padding: "2px 6px", borderRadius: 4, 
                                                background: t.finalSentiment.includes("Positive") ? "#DCFCE7" : "#FEE2E2",
                                                color: t.finalSentiment.includes("Positive") ? "#16A34A" : "#DC2626",
                                                fontWeight: 600
                                            }}>{t.finalSentiment}</span>
                                        )}
                                        {t.issueType && <span style={{ fontSize: 9, padding: "2px 6px", borderRadius: 4, background: "#F3F4F6", color: "#6B7280" }}>{t.issueType}</span>}
                                    </div>
                                </div>
                            ))}
                            {merchantTickets.length > 5 && (
                                <div style={{ textAlign: "center", fontSize: 11, color: "#6B7280", marginTop: 4 }}>
                                    + {merchantTickets.length - 5} more tickets
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = {
    root: { maxWidth: 1200, margin: "0 auto", padding: 24, fontFamily: "system-ui, sans-serif" },
    title: { fontSize: 24, fontWeight: 700, margin: 0 },
    subtitle: { color: "#6b7280", marginBottom: 20, fontSize: 13 },
    controls: { display: "flex", alignItems: "center", gap: 16, marginBottom: 16, flexWrap: "wrap" },
    toggleGroup: { display: "flex", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden", background: "#f9fafb" },
    toggleBtn: { padding: "8px 16px", border: "none", background: "transparent", cursor: "pointer", fontSize: 13, color: "#4b5563", transition: "all 0.15s" },
    toggleBtnActive: { background: "#fff", color: "#111827", fontWeight: 600, boxShadow: "0 1px 3px rgba(0,0,0,0.1)", borderRadius: 6, margin: 2 },
    inputGroup: { display: "flex", flexDirection: "column", gap: 12, marginBottom: 20 },
    input: { padding: "12px 14px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, outline: "none", transition: "border-color 0.2s" },
    textarea: { padding: "12px 14px", border: "1px solid #e5e7eb", borderRadius: 8, fontSize: 14, resize: "vertical", outline: "none" },
    analyzeBtn: { padding: "12px 28px", background: "#FF5A00", color: "#fff", border: "none", borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: "pointer", width: "100%", transition: "background 0.2s" },
    errorBanner: { marginTop: 16, padding: "12px 16px", background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 8, color: "#b91c1c", fontSize: 13 },
    liveDataBanner: { background: "#F3F4F6", border: "1px solid #E5E7EB", borderRadius: 8, padding: 16 },
    card: { border: "1px solid #e5e7eb", borderRadius: 12, padding: 24, marginTop: 24, background: "#fff", boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.05)" },
    cardTitle: { margin: 0, fontSize: 18, fontWeight: 700, color: "#111827" },
    scoreRow: { display: "flex", gap: 16, margin: "20px 0" },
    scoreBadge: { display: "flex", flexDirection: "column", alignItems: "center", background: "#f9fafb", border: "1px solid #f3f4f6", borderRadius: 12, padding: "12px 24px", flex: 1 },
    scoreNum: { fontSize: 28, fontWeight: 800, letterSpacing: "-0.5px" },
    scoreLabel: { fontSize: 11, color: "#6b7280", marginTop: 4, textTransform: "uppercase", fontWeight: 600 },
    field: { marginBottom: 16 },
    fieldLabel: { display: "block", fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase", marginBottom: 6, letterSpacing: "0.05em" },
    arabicText: { direction: "rtl", display: "block", fontFamily: "Tahoma, Arial, sans-serif", fontSize: 15, color: "#1f2937", lineHeight: 1.6 },
    pills: { display: "flex", flexWrap: "wrap", gap: 8, direction: "rtl" },
    pill: { background: "#f3f4f6", color: "#374151", border: "1px solid #e5e7eb", padding: "6px 12px", borderRadius: 20, fontSize: 13, fontWeight: 500 },
    pitchBox: { background: "#f9fafb", border: "1px solid #f3f4f6", borderRadius: 10, padding: 16, marginTop: 16 },
    pitchTextLight: { direction: "rtl", fontFamily: "Tahoma, Arial, sans-serif", fontSize: 14, lineHeight: 1.6, margin: 0, color: "#374151" },
    pitchText: { direction: "rtl", fontFamily: "Tahoma, Arial, sans-serif", fontSize: 15, lineHeight: 1.8, margin: 0 },
    objectionCard: { background: "#fff", border: "1px solid #e5e7eb", borderRadius: 8, padding: 12, direction: "rtl" },
    objectionText: { fontSize: 13, color: "#6b7280", marginBottom: 6 },
    responseText: { fontSize: 14, color: "#111827", fontWeight: 500 },
    loadingBox: { marginTop: 24, padding: 32, background: "#f9fafb", border: "1px dashed #d1d5db", borderRadius: 12, textAlign: "center" },
    streamPreview: { textAlign: "left", fontSize: 12, color: "#6b7280", maxHeight: 150, overflow: "hidden", marginTop: 16, direction: "ltr", fontFamily: "monospace", whiteSpace: "pre-wrap" },
    spinner: { width: 32, height: 32, border: "3px solid #e5e7eb", borderTopColor: "#FF5A00", borderRadius: "50%", animation: "spin 0.8s linear infinite", margin: "0 auto" },
};
